// progressForm.js
// Progress tracking component similar to C# ProgressForm

export class ProgressForm {
  constructor() {
    this.progressContainer = null;
    this.progressBar = null;
    this.progressLabel = null;
    this.currentProgress = 0;
    this.isVisible = false;
  }

  show() {
    if (this.isVisible) return;

    this.createProgressUI();
    this.isVisible = true;
    this.setProgress(0);
    
    // Initially hide close button
    const closeButton = document.getElementById("progressCloseButton");
    if (closeButton) {
      closeButton.style.display = "none";
    }
  }

  hide() {
    if (!this.isVisible) return;

    // Show final state for a moment like C# version
    setTimeout(() => {
      this.removeProgressUI();
      this.isVisible = false;
    }, 800);
  }

  setProgress(value) {
    this.currentProgress = value;
    
    if (!this.isVisible) return;

    const closeButton = document.getElementById("progressCloseButton");
    
    if (value === -1) {
      // Error state
      this.progressBar.style.width = "100%";
      this.progressBar.style.backgroundColor = "#dc3545";
      this.progressLabel.textContent = "Error occurred during upload";
      if (closeButton) {
        closeButton.style.display = "flex"; // Show close button
        closeButton.style.color = "#dc3545"; // Red color for error
      }
    } else if (value === 100) {
      // Success state
      this.progressBar.style.width = "100%";
      this.progressBar.style.backgroundColor = "#28a745";
      this.progressLabel.textContent = "Upload completed successfully";
      if (closeButton) {
        closeButton.style.display = "flex"; // Show close button
        closeButton.style.color = "#28a745"; // Green color for success
      }
    } else if (value >= 0 && value <= 100) {
      // Normal progress
      this.progressBar.style.width = `${value}%`;
      this.progressLabel.textContent = `Uploading... ${value}%`;
      
      // Hide close button during progress
      if (closeButton) {
        closeButton.style.display = "none";
      }
      
      // Update color based on progress
      if (value < 50) {
        this.progressBar.style.backgroundColor = "#007bff"; // Blue for email upload
      } else if (value < 80) {
        this.progressBar.style.backgroundColor = "#ffc107"; // Yellow for attachments
      } else {
        this.progressBar.style.backgroundColor = "#17a2b8"; // Cyan for final save
      }
    }
  }

  createProgressUI() {
    // Remove existing if any
    this.removeProgressUI();

    // Create progress container
    this.progressContainer = document.createElement("div");
    this.progressContainer.id = "progressContainer";
    this.progressContainer.style.cssText = `
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: white;
      border: 2px solid #dee2e6;
      border-radius: 8px;
      padding: 15px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      z-index: 10000;
      min-width: 300px;
      max-width: 350px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    `;

    // Create header with close button
    const header = document.createElement("div");
    header.style.cssText = `
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 12px;
    `;

    const headerText = document.createElement("div");
    headerText.textContent = "Uploading Email";
    headerText.style.cssText = `
      font-size: 14px;
      font-weight: 600;
      color: #333;
    `;

    // Create close button (X)
    const closeButton = document.createElement("button");
    closeButton.textContent = "×";
    closeButton.id = "progressCloseButton";
    closeButton.style.cssText = `
      background: none;
      border: none;
      font-size: 20px;
      color: #666;
      cursor: pointer;
      padding: 0;
      width: 24px;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 4px;
      transition: background-color 0.2s, color 0.2s;
    `;

    closeButton.addEventListener("mouseenter", () => {
      closeButton.style.backgroundColor = "#f0f0f0";
      closeButton.style.color = "#333";
    });

    closeButton.addEventListener("mouseleave", () => {
      closeButton.style.backgroundColor = "none";
      closeButton.style.color = "#666";
    });

    closeButton.addEventListener("click", () => {
      this.hide();
    });

    header.appendChild(headerText);
    header.appendChild(closeButton);

    // Create progress bar container
    const progressContainer = document.createElement("div");
    progressContainer.style.cssText = `
      background: #e9ecef;
      border-radius: 4px;
      height: 16px;
      overflow: hidden;
      margin-bottom: 8px;
    `;

    // Create progress bar
    this.progressBar = document.createElement("div");
    this.progressBar.style.cssText = `
      height: 100%;
      background: #007bff;
      transition: width 0.3s ease, background-color 0.3s ease;
      border-radius: 4px;
    `;

    progressContainer.appendChild(this.progressBar);

    // Create progress label
    this.progressLabel = document.createElement("div");
    this.progressLabel.style.cssText = `
      font-size: 12px;
      color: #666;
      text-align: center;
    `;

    // Create status details
    const statusDetails = document.createElement("div");
    statusDetails.id = "statusDetails";
    statusDetails.style.cssText = `
      font-size: 10px;
      color: #999;
      margin-top: 8px;
      text-align: center;
    `;

    // Assemble the UI
    this.progressContainer.appendChild(header);
    this.progressContainer.appendChild(progressContainer);
    this.progressContainer.appendChild(this.progressLabel);
    this.progressContainer.appendChild(statusDetails);

    // Add to document
    document.body.appendChild(this.progressContainer);
  }

  removeProgressUI() {
    if (this.progressContainer && this.progressContainer.parentNode) {
      this.progressContainer.parentNode.removeChild(this.progressContainer);
      this.progressContainer = null;
      this.progressBar = null;
      this.progressLabel = null;
    }
  }

  updateStatus(message) {
    const statusDetails = document.getElementById("statusDetails");
    if (statusDetails) {
      statusDetails.textContent = message;
    }
  }

  // Static method to show progress form (similar to C# ShowOnNewThread)
  static show() {
    const progressForm = new ProgressForm();
    progressForm.show();
    return progressForm;
  }
}

// Progress tracking class for upload operations
export class UploadProgressTracker {
  constructor() {
    this.progressForm = null;
    this.currentStage = "";
    this.totalStages = 3;
  }

  start() {
    this.progressForm = ProgressForm.show();
    this.setProgress(0, "Preparing upload...");
  }

  setProgress(value, status = "") {
    if (this.progressForm) {
      this.progressForm.setProgress(value);
      if (status) {
        this.progressForm.updateStatus(status);
      }
    }
  }

  // Email upload stage (0-50%)
  setProgressForEmailUpload(current, total) {
    const percentage = Math.round((current / total) * 50);
    this.setProgress(percentage, `Uploading email file... (${current}/${total})`);
  }

  // Attachments upload stage (50-80%)
  setProgressForAttachments(current, total) {
    const basePercentage = 50;
    const attachmentPercentage = total > 0 ? (current / total) * 30 : 30;
    const totalPercentage = basePercentage + attachmentPercentage;
    this.setProgress(Math.round(totalPercentage), `Uploading attachments... (${current}/${total})`);
  }

  // Final save stage (80-100%)
  setProgressForFinalSave(stage, total) {
    const basePercentage = 80;
    const savePercentage = (stage / total) * 20;
    const totalPercentage = basePercentage + savePercentage;
    
    const stageNames = ["Saving to database...", "Finalizing...", "Completing..."];
    this.setProgress(Math.round(totalPercentage), stageNames[stage] || "Processing...");
  }

  error(message) {
    if (this.progressForm) {
      this.progressForm.setProgress(-1);
      this.progressForm.updateStatus(message);
    }
  }

  complete() {
    if (this.progressForm) {
      this.progressForm.setProgress(100);
      setTimeout(() => {
        this.progressForm.hide();
      }, 100);
    }
  }

  close() {
    if (this.progressForm) {
      this.progressForm.hide();
    }
  }
}
